import { useMutation } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { Col, Form, InputNumber, Modal, Row, Spin } from 'antd';
import GoongMapApi from 'apis/goongMapApis';
import { Dispatch, forwardRef, memo, useEffect, useRef, useState } from 'react';
import { toastError } from 'utils/constants/functions';

interface IProps {
  open: boolean;
  title: any;
  center: any;
  scale?: number;
  placement?: string;
  className?: string;
  setOpen: Dispatch<boolean>;
  updateLocation?: Dispatch<any>;
}

const ModalMapLocation = forwardRef<any, IProps>((props: IProps | any, ref) => {
  const {
    open,
    title,
    center,
    placement,
    scale = 1,
    setOpen,
    updateLocation,
  } = props;

  const mapRef = useRef<google.maps.Map>(); // Lưu trữ tham chiếu đến đối tượng bản đồ
  const infoWindowRef = useRef<google.maps.InfoWindow>(); // Lưu trữ tham chiếu đến InfoWindow
  const markerRef = useRef<any>(); // Lưu trữ tham chiếu đến Marker
  const [isLoading, setIsLoading] = useState(false);

  const [value, setValue] = useState('');
  const valueDebounce = useDebounce(value, 300);
  // const [options, setOptions] = useState([]);
  const [point, setPoint] = useState<any>('');
  const [lat, setLat] = useState<number>();
  const [lng, setLng] = useState<number>();
  // const controlDivRef = useRef<HTMLDivElement | null>(null);
  const mapIdRef = useRef<HTMLDivElement | null>(null);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  let map: google.maps.Map, infoWindow: google.maps.InfoWindow;

  const { mutate: getSearchPlace, data: places } = useMutation({
    mutationFn: GoongMapApi.getPlacesSearchBykeyword,
    onSuccess: (response: any) => {
      // setOptions(
      //   response.status == 'OK'
      //     ? response.predictions.map((item: any) => ({
      //         label: item.description,
      //         value: item.place_id,
      //       }))
      //     : [],
      // );

      if (response.status !== 'OK') return;
      getPlaceDetail(response.predictions[0].place_id);
    },
  });

  const { mutate: getPlaceDetail, data: placeDetail } = useMutation({
    mutationFn: (placeId: string) => GoongMapApi.getPlaceDetailById(placeId),
    onSuccess: async (response: any) => {
      updateLocation(response.result.geometry.location);
      setPoint(response.result.geometry.location);
      initMap(response.result.geometry.location);
    },
  });

  const { mutateAsync: getReverseGeocoding, data: reverseGeocoding } =
    useMutation({
      mutationFn: GoongMapApi.getReverseGeocoding,
      onSuccess: (response: any) => {
        if (markerRef.current && infoWindowRef.current) {
          // markerRef.current.setAttribute(
          //   'title',
          //   response?.results?.[0].formatted_address,
          // );
          markerRef.current.addListener('click', (domEvent: any) => {
            if (infoWindowRef.current) {
              infoWindowRef.current.close();
              infoWindowRef.current.setContent(
                response?.results?.[0].formatted_address,
              );
              infoWindowRef.current.open(
                markerRef.current.map,
                markerRef.current,
              );
            }
          });
        }
      },
      onError: (error) => {
        if (markerRef.current && infoWindowRef.current) {
          markerRef.current.addListener('click', (domEvent: any) => {
            // const { target } = domEvent;
            if (infoWindowRef.current) {
              infoWindowRef.current.close();
              infoWindowRef.current.setContent(markerRef.current.title);
              infoWindowRef.current.open(
                markerRef.current.map,
                markerRef.current,
              );
            }
          });
        }
      },
    });

  const handleReverseGeocoding = async (latLng: {
    lat: number;
    lng: number;
  }) => {
    try {
      await getReverseGeocoding(latLng);
    } catch (error: any) {
      if (error.response?.status === 429) {
        toastError('Bạn đã gửi quá nhiều yêu cầu. Vui lòng thử lại sau.');
        return;
      }

      toastError('Không thể lấy thông tin vị trí. Vui lòng thử lại sau.');
      return;
    }
  };

  async function initMap(valueSearch?: any): Promise<void> {
    const { Map } = (await google.maps.importLibrary(
      'maps',
    )) as google.maps.MapsLibrary;

    const { AdvancedMarkerElement, PinElement } =
      (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    infoWindow = new google.maps.InfoWindow();

    function getMapCenter(lat: number, lng: number, zoom = 15) {
      if (!mapIdRef.current) {
        const defaultDiv = document.createElement('div');
        defaultDiv.id = 'map';

        document.body.appendChild(defaultDiv);
        mapIdRef.current = defaultDiv;
      }

      return new Map(
        mapIdRef.current,
        // ? mapIdRef.current
        // : (document.getElementById('map') as HTMLElement),
        {
          center: {
            lat,
            lng,
          },
          zoom,
          // mapId: 'AIzaSyA1MgLuZuyqR_OGY3ob3M52N46TDBRI_9k', //chuyển sang .env
          mapId:
            process.env.REACT_APP_GOOGLE_MAP_ID ||
            'AIzaSyA1MgLuZuyqR_OGY3ob3M52N46TDBRI_9k', //chuyển sang .env
          // mapId: 'AIzaSyA1MgLuZuyqR_OGY3ob3M52N46TDBRI_9k',
        },
      );
    }

    const getCurrentPosition = (): Promise<GeolocationPosition> => {
      return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        } else {
          reject(new Error('Geolocation is not supported by this browser.'));
        }
      });
    };

    if (!center?.lat || !center?.lng) {
      if (navigator.geolocation) {
        setIsLoading(true);
        try {
          const position = await getCurrentPosition();

          map = getMapCenter(
            position.coords.latitude,
            position.coords.longitude,
          );
        } catch (e) {
          map = getMapCenter(10.767644104951271, 106.6559225320816);
        } finally {
          setIsLoading(false);
        }
      } else map = getMapCenter(10.767644104951271, 106.6559225320816);
    } else map = getMapCenter(center.lat, center.lng);

    mapRef.current = map;
    infoWindowRef.current = infoWindow;

    const pin = new PinElement({
      scale: scale,
    });

    //button pan to current location
    if (updateLocation) {
      // console.log('into updateLocation', valueSearch);

      // if (controlDivRef.current) {
      //   controlDivRef.current.style.display = 'flex';
      //   setTimeout(() => {
      //     if (controlDivRef.current)
      //       map.controls[
      //         google.maps.ControlPosition.BLOCK_START_INLINE_CENTER
      //       ].push(controlDivRef.current);
      //   }, 400);
      // }

      map.addListener('click', async (mapsMouseEvent: any) => {
        infoWindow.close();
        const latlng = mapsMouseEvent.latLng.toJSON();

        setLat(latlng.lat);
        setLng(latlng.lng);
        setPoint(latlng);
        createMaker(latlng);

        // getReverseGeocoding(latlng); //search AutoComplete
        await handleReverseGeocoding(latlng);
      });

      const locationButton = document.createElement('button');
      locationButton.textContent = 'Vị trí ban đầu';
      locationButton.classList.add('custom-map-control-button');
      // map.controls[google.maps.ControlPosition.TOP_CENTER].push(locationButton);

      locationButton.addEventListener('click', () => {
        infoWindow.close();
        createMaker(center);
        if (!center?.lat || !center?.lng) {
          toastError('Khách hàng chưa có vị trí');
          return;
        }
        map.setCenter(center);
        map.setZoom(15);
      });
    }

    // Tạo marker
    const createMaker = (latLng: any, title?: any) => {
      if (
        latLng.lat === null ||
        latLng.lng === null ||
        latLng.lat === undefined ||
        latLng.lng === undefined ||
        typeof parseFloat(latLng.lat) != 'number' ||
        typeof parseFloat(latLng.lng) != 'number'
      )
        return;

      const marker = new AdvancedMarkerElement({
        map,
        position: latLng,
        content: pin.element,
        title: `(${latLng.lat}, ${latLng.lng})`,
        gmpClickable: true,
      });

      // console.log('create marker', marker);

      // marker.addListener('click', (domEvent: any) => {
      //   // const { target } = domEvent;
      //   infoWindow.close();
      //   infoWindow.setContent(marker.title);
      //   infoWindow.open(marker.map, marker);
      // });
      // console.log(marker);

      markerRef.current = marker;
    };

    if (!valueSearch) createMaker(center);
    else {
      infoWindow.close();
      createMaker(valueSearch);
      map.setCenter(valueSearch || { lat: 0, lng: 0 });
      map.setZoom(15);
    }
  }

  useEffect(() => {
    if (!open) {
      setPoint('');
      setValue('');
      setLat(undefined);
      setLng(undefined);
      return;
    }

    if (
      center?.lat === null ||
      center?.lng === null ||
      center?.lat === undefined ||
      center?.lng === undefined
    ) {
      initMap(undefined);
    } else {
      initMap(center);
      setPoint(center);
      setLat(center?.lat);
      setLng(center?.lng);

      // getReverseGeocoding(center);
      handleReverseGeocoding(center);
    }
  }, [open]);

  useEffect(() => {
    if (!placement) return;
    placement && setValue(placement);
  }, [placement]);

  useEffect(() => {
    if (!valueDebounce) return;
    getSearchPlace({ input: valueDebounce });
  }, [valueDebounce]);

  const onSelect = (data: any, option: any) => {
    setValue(option.label);
    getPlaceDetail(data);
  };

  const onSearch = (searchText: any) => {
    setValue(searchText);
  };

  const checkLatLng = (lat: any, lng: any) => {
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toastError('Tọa độ không hợp lệ');
      return;
    }
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    if (lat !== '' && lng !== '') {
      setPoint({ lat, lng });
      timeoutId.current = setTimeout(() => {
        initMap({ lat, lng });
      }, 500);
    }
  };

  const hanldeOkModal = () => {
    updateLocation(point);
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      title={title}
      okText="Lưu"
      cancelText="Hủy"
      onCancel={() => {
        setOpen(false);
      }}
      onOk={hanldeOkModal}
      width={'80%'}
      {...(updateLocation ? { closable: false } : { footer: true })}
    >
      <Form>
        {/* <Form.Item label="Tìm kiếm địa chỉ" className="m-0 p-0 w-full">
          <AutoComplete
            options={options}
            className="w-6/12 mb-2"
            value={value}
            onSelect={onSelect}
            onSearch={onSearch}
            placeholder="Nhập vị trí"
            allowClear
          />
        </Form.Item> */}
        <Spin spinning={isLoading} size="large">
          <div className="relative">
            <div
              id="map"
              ref={mapIdRef}
              className={`h-[65vh] bg-gray-400 ${isLoading && 'hidden'}`}
            />
            <Row
              gutter={10}
              className={`absolute top-[14px] right-[55px] ${isLoading && 'hidden'}`}
              // className={`mt-4 right-[55px]`}
              // ref={controlDivRef}
              justify="end"
            >
              <Col className="w-[200px]">
                <Form.Item className="m-0 p-0">
                  <InputNumber
                    placeholder="Toạ độ X"
                    className="w-full"
                    value={lat}
                    onChange={(value) => {
                      setLat(value as number);
                      checkLatLng(value, lng);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col className="w-[200px]">
                <Form.Item className="m-0 p-0">
                  <InputNumber
                    placeholder="Toạ độ Y"
                    className="w-full"
                    value={lng}
                    onChange={(value) => {
                      setLng(value as number);
                      checkLatLng(lat, value);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
          {isLoading && <div className={`h-[65vh] bg-gray-300`} />}
        </Spin>
      </Form>
    </Modal>
  );
});

export default memo(ModalMapLocation);
